/* eslint-disable max-lines */
import React, { useState, createRef, useEffect } from 'react';
import { FormControlLabel } from '@material-ui/core';
import ChevronRight from '@material-ui/icons/ChevronRight';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import { Flex, Padding } from 'components/base/layout-components';
import Checkbox from 'components/base/Checkbox';
import ToggleBox from 'components/ToggleBox';
import Text, { Inline } from 'components/base/Text';
import styled from 'styled-components';
import _ from 'lodash';
import StyledAvatar from 'components/StyledAvatar';
import { getColor } from 'common/constants/_colors';
import * as TrackingService from 'common/services/tracking.service';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import {
  SESSION_STORAGE_KEYS,
  LOCAL_STORAGE_KEYS,
  TRACKING
} from 'common/constants/constants';

const PeopleFilterSpacing = styled.div`
  padding: 1rem;
  padding-top: 0;
`;

const PeopleFilter = styled.div`
  position: relative;
  &:hover {
    .navbutton {
      display: block;
    }
  }
`;

const PeopleFilterWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  margin-bottom: -1px;
`;

const PeopleBox = styled.div`
  transform: translate(0, 0);
  transition: transform 150ms ease-out;
`;

const PeopleWrapper = styled(Flex)``;

const Separator = styled(Inline)`
  color: ${getColor('pigeonPost')};
  padding: 0 0.6rem;
`;

const PersonBox = styled(Flex)`
  min-width: 15.5rem;
  flex-grow: 1;
  padding: 2rem 3rem 1.5rem 1.5rem;
  border-bottom: 1px solid ${getColor('botticelli')};
  &:not(:last-child) {
    border-right: 1px solid ${getColor('botticelli')};
  }
  ${({ total }) =>
    total < 5
      ? `max-width: 18rem; border-right: 1px solid ${getColor(
          'botticelli'
        )} !important;`
      : ''};
  &.unselected {
    background-color: ${getColor('catskillGray')};
  }
  &.selected {
    background-color: ${getColor('white')};
    border-bottom: 1px solid ${getColor('white')};
  }
  &:hover {
    box-shadow: 0px 0px 34px rgba(59, 89, 152, 0.2);
    cursor: pointer;
  }
`;

const PersonData = styled(Flex)`
  padding-left: 1rem;
  flex-grow: 1;
  & > div {
    padding-bottom: 0.5rem;
  }
`;

const PersonProgressBar = styled.div`
  width: 100%;
  height: 0.4rem;
  line-height: 0.4rem;
  border-radius: 10px;
`;

const navButtonStyle = {
  width: '1.8rem',
  height: '1.8rem',
  position: 'absolute',
  left: '-0.6rem',
  top: '50%',
  transform: 'translate(0, -50%)',
  borderRadius: '50%',
  border: `1px solid ${getColor('pigeonPost')}`,
  backgroundColor: `${getColor('white')}`,
  cursor: 'pointer',
  display: 'none',
  '&:hover': {
    borderColor: `${getColor('dodgerBlue')}`,
    backgroundColor: `${getColor('fog')}`,
    'svg *': {
      fill: `${getColor('dodgerBlue')}`
    }
  }
};

const arrowStyle = {
  color: `${getColor('nepal')}`,
  margin: '0',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

const Prev = styled.div`
  ${navButtonStyle}
`;

const Next = styled.div`
  ${navButtonStyle}
  left: unset;
  right: -0.6rem;
`;

let resizeTimeout;

// eslint-disable-next-line max-lines-per-function
const PulsePeopleFilter = ({
  data,
  teamId,
  teamName,
  selectedContributorId = [],
  setSelectedContributorId,
  issuesDataLength,
  filteredDataLength
}) => {
  const [selectedPerson, setSelectedPerson] = useState([]);
  const sessionStorageAccessor = BrowserStorageAccessor().session;
  const localStorageAccessor = BrowserStorageAccessor().local;
  const [isTeamMembersOnly, setIsTeamMembersOnly] = useState(false);
  const [position, setPosition] = useState(0);
  const [showButtons, setShowButtons] = useState('next');
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(
    sessionStorageAccessor.get(SESSION_STORAGE_KEYS.CLOSED_PEOPLE_FILTER)
  );
  const [shouldCalculateResize, setShouldCalculateResize] = useState(false);
  const [shouldCaptureWheel, setShouldCaptureWheel] = useState(true);

  const wrapperRef = createRef();
  const containerRef = createRef();

  const teamMembersIds = data.map(({ id }) => id);

  const selectPerson = id => {
    TrackingService.logEvent(TRACKING.EVENTS.FILTER, {
      page: TRACKING.PAGES.PULSE,
      filter_name: TRACKING.FILTER.CONTRIBUTOR,
      team_name: teamName,
      filter_value: id !== selectedContributorId[0]
    });
    setSelectedPerson(id === selectedPerson[0] ? [] : [id]);
    setSelectedContributorId(
      id === selectedPerson[0]
        ? isTeamMembersOnly
          ? teamMembersIds
          : []
        : [id]
    );
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (shouldCalculateResize) {
      setShouldCalculateResize(false);
      handleShowButtons();
    }
  }, [shouldCalculateResize]); // eslint-disable-line

  useEffect(() => {
    if (data.length && !isFilterCollapsed) {
      handleShowButtons();
      showTeamMembersOnly(
        localStorageAccessor.get(
          LOCAL_STORAGE_KEYS.SHOW_ONLY_TEAMS_WORK(teamId)
        )
      );
    }
  }, [position, data, isFilterCollapsed]); // eslint-disable-line

  useEffect(() => {
    if (wrapperRef.current !== null && shouldCaptureWheel) {
      setShouldCaptureWheel(false);
      wrapperRef.current.addEventListener('wheel', _preventWheel, {
        passive: false
      });
    }
  }, [wrapperRef, shouldCaptureWheel]);

  const handleResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => setShouldCalculateResize(true), 200);
  };

  const handleShowButtons = () => {
    if (wrapperRef.current !== null) {
      if (position > 0) {
        if (
          position >
          wrapperRef.current.scrollWidth - containerRef.current.offsetWidth
        ) {
          setPosition(
            wrapperRef.current.scrollWidth - containerRef.current.offsetWidth
          );
        } else if (
          containerRef.current.offsetWidth < wrapperRef.current.scrollWidth &&
          position <
            wrapperRef.current.scrollWidth - containerRef.current.offsetWidth
        ) {
          setShowButtons('both');
        } else {
          setShowButtons('previous');
        }
      } else {
        if (
          containerRef.current.offsetWidth < wrapperRef.current.scrollWidth &&
          position <
            wrapperRef.current.scrollWidth - containerRef.current.offsetWidth
        ) {
          setShowButtons('next');
        } else {
          setShowButtons('none');
        }
      }
    }
  };

  const _previous = () => {
    const _position = Math.max(position - containerRef.current.offsetWidth, 0);
    setPosition(_position);
  };
  const _next = () => {
    const _position = Math.min(
      position + containerRef.current.offsetWidth,
      wrapperRef.current.scrollWidth - containerRef.current.offsetWidth
    );
    setPosition(_position);
  };

  const _preventWheel = e => {
    e.preventDefault();
  };

  const _scroll = e => {
    const _position = position + e.deltaX;
    if (_position < 0) setPosition(0);
    else if (
      _position >
      wrapperRef.current.scrollWidth - containerRef.current.offsetWidth
    )
      setPosition(
        wrapperRef.current.scrollWidth - containerRef.current.offsetWidth
      );
    else setPosition(_position);
  };

  const toggleFilter = () => {
    const toggleCollapse = !isFilterCollapsed;
    if (toggleCollapse) {
      sessionStorageAccessor.set(SESSION_STORAGE_KEYS.CLOSED_PEOPLE_FILTER);
    } else {
      sessionStorageAccessor.remove(SESSION_STORAGE_KEYS.CLOSED_PEOPLE_FILTER);
    }
    setIsFilterCollapsed(toggleCollapse);
  };

  const showTeamMembersOnly = checked => {
    setIsTeamMembersOnly(checked);
    setSelectedPerson([]);
    setSelectedContributorId(checked ? teamMembersIds : []);
    if (checked) {
      localStorageAccessor.set(
        LOCAL_STORAGE_KEYS.SHOW_ONLY_TEAMS_WORK(teamId),
        true
      );
    } else {
      localStorageAccessor.remove(
        LOCAL_STORAGE_KEYS.SHOW_ONLY_TEAMS_WORK(teamId)
      );
    }
  };

  const membersTotal = data.length;

  return (
    <PeopleFilterSpacing data-cy='team-members-filters-row'>
      <ToggleBox
        collapsed={isFilterCollapsed}
        onClick={() => {
          toggleFilter();
        }}
        title={
          <>
            TEAM MEMBERS ({membersTotal})<Separator>|</Separator>click a member
            to filter their stories below
            <Padding
              l='small'
              onClick={e => {
                e.stopPropagation();
              }}
            >
              {membersTotal ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      disableRipple
                      style={{ padding: '0px 0.5rem 0 2rem' }}
                      checked={isTeamMembersOnly}
                      onChange={e => {
                        showTeamMembersOnly(e.target.checked);
                      }}
                    />
                  }
                  label={
                    <Text size='small' weight='500'>
                      Show only team’s work
                      {/* ({filteredDataLength}/
                      {issuesDataLength}) */}
                    </Text>
                  }
                />
              ) : null}
            </Padding>
          </>
        }
      >
        {!isFilterCollapsed && (
          <PeopleFilter>
            <PeopleFilterWrapper ref={containerRef}>
              <PeopleBox
                onWheel={e => {
                  return _scroll(e);
                }}
                ref={wrapperRef}
                style={{ transform: `translate(${-position}px, 0)` }}
              >
                <PeopleWrapper direction='row'>
                  {data &&
                    _.orderBy(data, ['in-progress'], ['desc']).map(d => {
                      const { id, avatar, name, 'in-progress': wip } = d;

                      const selectedClass = selectedPerson.length
                        ? selectedPerson.includes(id)
                          ? 'selected'
                          : 'unselected'
                        : '';
                      const wipPercent = wip >= 5 ? 100 : wip * 20;
                      const wipColor =
                        wip >= 5 ? getColor('orangePeel') : getColor('nepal');

                      return (
                        <PersonBox
                          data-cy='person-box'
                          key={id}
                          onClick={() => {
                            selectPerson(id);
                          }}
                          total={membersTotal}
                          className={selectedClass}
                        >
                          <Flex direction='row' width='100%' items='flex-start'>
                            <StyledAvatar
                              avatar={{
                                url: avatar,

                                isGrayedOut: selectedContributorId.length
                                  ? selectedContributorId.includes(id)
                                    ? false
                                    : true
                                  : false
                              }}
                              disableTooltip={true}
                              alt=''
                            />
                            <PersonData direction='column'>
                              <Text
                                size='medium'
                                mb='0.4rem'
                                multiLineEllipsisAfter={1}
                              >
                                {name}
                              </Text>
                              <div>
                                <PersonProgressBar
                                  style={{
                                    backgroundColor: wipColor,
                                    width: `${wipPercent}%`
                                  }}
                                />
                              </div>
                              <div>
                                {wip} Wip
                                {/* TODO: uncomment after fixing the bug
                                <Separator>|</Separator>
                                <Inline color='shuttleGray'>
                                  {branches} Branches
                                </Inline> */}
                              </div>
                            </PersonData>
                          </Flex>
                        </PersonBox>
                      );
                    })}
                </PeopleWrapper>
              </PeopleBox>
            </PeopleFilterWrapper>
            {showButtons.match(/(both|previous)/) && (
              <Prev className='navbutton' onClick={_previous}>
                <ChevronLeft style={arrowStyle} />
              </Prev>
            )}
            {showButtons.match(/(both|next)/) && (
              <Next className='navbutton' onClick={_next}>
                <ChevronRight style={arrowStyle} />
              </Next>
            )}
          </PeopleFilter>
        )}
      </ToggleBox>
    </PeopleFilterSpacing>
  );
};

export default PulsePeopleFilter;
